// Player settings (2026-10-25, ui branch): game speed, graphics and camera distance, kept in localStorage. Pure apart
// from the storage handed in, so tests/ui.test.ts checks that a bad or old saved value falls back to the default.
// Graphics "best" draws the 3D armies at the screen's pixel ratio (up to 2); "fast" draws the placeholder soldiers at
// ratio 1, the same as ?premium=0. Depth blur is Best's tilt-shift (the table's near and far edges soft, like a photo
// of a model); ?dof=0 is the same as Off. Weather effects Off (2026-10-26) keeps the season and the time of day but no
// rain, snow, clouds or anything falling (sky.ts). A URL param still wins for its session and is never saved.

export type Speed = 1 | 1.5 | 2;
export type Quality = 'best' | 'fast';
export type CameraDist = 'close' | 'normal' | 'far';
export type Blur = 'on' | 'off';
export type Weather = 'on' | 'off';
export type Settings = { speed: Speed; quality: Quality; autoQuality: boolean; camera: CameraDist; blur: Blur; weather: Weather; sound: Blur; music: Blur; tips: Blur };

export const DEFAULTS: Settings = { speed: 1, quality: 'best', autoQuality: true, camera: 'normal', blur: 'on', weather: 'on', sound: 'on', music: 'on', tips: 'on' };
export const SPEEDS: readonly Speed[] = [1, 1.5, 2];
export const QUALITIES: readonly Quality[] = ['best', 'fast'];
export const CAMERAS: readonly CameraDist[] = ['close', 'normal', 'far'];
export const BLURS: readonly Blur[] = ['on', 'off'];
export const WEATHERS: readonly Weather[] = ['on', 'off'];
/** How far the camera sits against the normal distance. */
export const CAMERA_ZOOM: Record<CameraDist, number> = { close: 0.75, normal: 1, far: 1.3 };
export const KEY = 'footmen-wars.settings';

/** Settings from a saved string; every field that is missing or not allowed takes its default. */
export function parseSettings(raw: string | null | undefined): Settings {
  let o: Partial<Record<keyof Settings, unknown>> = {};
  try { const v = raw ? JSON.parse(raw) : {}; if (v && typeof v === 'object') o = v; } catch { /* a broken save is the defaults */ }
  return {
    speed: (SPEEDS as readonly unknown[]).includes(o.speed) ? (o.speed as Speed) : DEFAULTS.speed,
    quality: (QUALITIES as readonly unknown[]).includes(o.quality) ? (o.quality as Quality) : DEFAULTS.quality,
    autoQuality: typeof o.autoQuality === 'boolean' ? o.autoQuality : DEFAULTS.autoQuality,
    camera: (CAMERAS as readonly unknown[]).includes(o.camera) ? (o.camera as CameraDist) : DEFAULTS.camera,
    blur: (BLURS as readonly unknown[]).includes(o.blur) ? (o.blur as Blur) : DEFAULTS.blur,
    sound: o.sound === 'off' ? 'off' : 'on',
    music: o.music === 'off' ? 'off' : 'on',
    tips: o.tips === 'off' ? 'off' : 'on',
    weather: (WEATHERS as readonly unknown[]).includes(o.weather) ? (o.weather as Weather) : DEFAULTS.weather,
  };
}

type Store = Pick<Storage, 'getItem' | 'setItem'> | undefined;
const store = (): Store => { try { return globalThis.localStorage; } catch { return undefined; } };

export function loadSettings(s: Store = store()): Settings {
  try { return parseSettings(s?.getItem(KEY)); } catch { return { ...DEFAULTS }; }
}
export function saveSettings(v: Settings, s: Store = store()) {
  try { s?.setItem(KEY, JSON.stringify(v)); } catch { /* private mode or full: the choice holds for this visit */ }
}

/** Separate from the settings payload so changing graphics cannot erase the first-match flag. */
export const TIPS_KEY = 'footmen-wars.tips-seen';
/** The army you last marched with: main.ts saves it when your pick is accepted (and plays its music on the menus), the
 *  army pick opens on it. One key since 2026-10-31 (the clarity pass had added a second, footmen-wars.army, and lost
 *  every saved pick). */
export const ARMY_KEY = 'footmen-wars.last-army';
/** A deliberate Graphics pick, including the already selected button, opts out for future visits. */
export function manualQuality(settings: Settings): Settings {
  return { ...settings, autoQuality: false };
}
